// front/src/utils/parseApiError.js

function firstNonEmptyString(x) {
  if (typeof x === "string") {
    const s = x.trim();
    return s ? s : null;
  }
  return null;
}

function pickFirstFieldMessage(fields) {
  if (!fields) return null;

  // Case: fields is array: ["msg", ...]
  if (Array.isArray(fields)) {
    for (const it of fields) {
      const s = firstNonEmptyString(it);
      if (s) return s;
    }
    return null;
  }

  // Case: fields is object: { otp: ["msg"], email: ["msg2"] } (possibly nested)
  if (typeof fields === "object") {
    for (const key of Object.keys(fields)) {
      const val = fields[key];

      // direct string
      const direct = firstNonEmptyString(val);
      if (direct) return direct;

      // array
      if (Array.isArray(val)) {
        for (const it of val) {
          const s = firstNonEmptyString(it);
          if (s) return s;
        }
      }

      // nested object
      if (val && typeof val === "object" && !Array.isArray(val)) {
        const nested = pickFirstFieldMessage(val);
        if (nested) return nested;
      }
    }
  }

  return null;
}

function looksLikeBackendErrorObject(obj) {
  if (!obj || typeof obj !== "object") return false;
  // canonical: { message, code, extra }
  if (typeof obj.message === "string" && typeof obj.code === "string") return true;
  // sometimes: { detail, code, extra }
  if (typeof obj.detail === "string" && typeof obj.code === "string") return true;
  // sometimes: { message, extra: {...} } or { detail, extra: {...} }
  if (
    (typeof obj.message === "string" || typeof obj.detail === "string") &&
    typeof obj.extra === "object" &&
    obj.extra !== null
  )
    return true;
  // sometimes: { code, extra }
  if (typeof obj.code === "string" && typeof obj.extra === "object" && obj.extra !== null) return true;

  return false;
}

/**
 * ✅ unwrap common wrappers around AxiosError / backend error objects.
 * react-query or other libs may wrap errors like:
 *   { error: AxiosError }, { cause: AxiosError }, { originalError: AxiosError }, etc.
 * We try a few hops but keep it safe + bounded.
 */
function unwrapPossibleWrappedError(input) {
  let cur = input;

  // fast path
  if (!cur || typeof cur !== "object") return cur;

  // If it already looks like axios error (has response) or backend object, return it
  if (cur.response || looksLikeBackendErrorObject(cur)) return cur;

  // bounded unwrapping (avoid loops)
  for (let i = 0; i < 6; i++) {
    if (!cur || typeof cur !== "object") break;

    // Common wrappers
    const next =
      cur.error ||
      cur.err ||
      cur.cause ||
      cur.originalError ||
      cur.original ||
      cur.reason ||
      cur.value;

    if (!next || next === cur) break;

    cur = next;

    if (cur && typeof cur === "object") {
      if (cur.response || looksLikeBackendErrorObject(cur)) return cur;
    }
  }

  return cur;
}

export function parseApiError(err) {
  // ✅ unwrap first so we don't miss err.response.data
  const e = unwrapPossibleWrappedError(err);

  // ✅ FIX: "already normalized" must be STRICT, otherwise AxiosError may pass accidentally
  // We require at least: message (string) + raw + isNetworkError + fieldErrors key
  const isAlreadyNormalized =
    e &&
    typeof e === "object" &&
    typeof e.message === "string" &&
    Object.prototype.hasOwnProperty.call(e, "fieldErrors") &&
    Object.prototype.hasOwnProperty.call(e, "isNetworkError") &&
    Object.prototype.hasOwnProperty.call(e, "raw");

  if (isAlreadyNormalized) {
    return e;
  }

  // ✅ If it's a string error (from rejectWithValue("...")), keep it as message
  if (typeof e === "string") {
    return {
      status: undefined,
      code: undefined,
      message: e,
      fieldErrors: undefined,
      isNetworkError: false,
      raw: e,
    };
  }

  // ✅ NEW: thunk payload can be a backend-normalized object (no err.response)
  // Example: rejectWithValue(ex.response.data) => {message, code, extra}
  if (looksLikeBackendErrorObject(e)) {
    const data = e;

    const code =
      (typeof data?.code === "string" && data.code) ||
      (typeof data?.extra?.code === "string" && data.extra.code) ||
      undefined;

    // validation fields (backend shape)
    const fields = data?.extra?.fields;

    if (fields) {
      if (Array.isArray(fields)) {
        const first = pickFirstFieldMessage(fields);
        return {
          status: undefined,
          code: code || "validation_error",
          message: first || data?.message || data?.detail || "اطلاعات فرم معتبر نیست.",
          fieldErrors: undefined,
          isNetworkError: false,
          raw: data,
        };
      }

      if (typeof fields === "object") {
        const fieldErrors = {};
        const allMessages = [];

        for (const [field, value] of Object.entries(fields)) {
          let msgs = [];

          if (Array.isArray(value)) {
            msgs = value
              .map((v) => (typeof v === "string" ? v.trim() : String(v)))
              .filter((v) => typeof v === "string" && v.trim());
          } else if (typeof value === "string") {
            const s = value.trim();
            msgs = s ? [s] : [];
          } else if (value == null) {
            msgs = [];
          } else if (typeof value === "object") {
            const nestedFirst = pickFirstFieldMessage(value);
            msgs = nestedFirst ? [nestedFirst] : [];
          } else {
            msgs = [String(value)];
          }

          if (msgs.length) {
            fieldErrors[field] = msgs;
            allMessages.push(...msgs);
          } else {
            fieldErrors[field] = [];
          }
        }

        const first = pickFirstFieldMessage(fields) || allMessages[0];

        return {
          status: undefined,
          code: code || "validation_error",
          // ✅ FIX: اگر fields پیام واقعی نداشت (مثل non_field_errors: [])
          // message را null بگذار تا لایه بالاتر با code ترجمه کند.
          message: first || null,
          fieldErrors,
          isNetworkError: false,
          raw: data,
        };
      }
    }

    const message =
      (typeof data?.message === "string" && data.message) ||
      (typeof data?.detail === "string" && data.detail) ||
      "خطایی رخ داد.";

    return {
      status: undefined,
      code,
      message,
      fieldErrors: undefined,
      isNetworkError: false,
      raw: data,
    };
  }

  // axios error path
  const res = e?.response;
  const data = res?.data;
  const status = res?.status;

  // ✅ Extract code (non-breaking)
  const code =
    (typeof data?.code === "string" && data.code) ||
    (typeof data?.extra?.code === "string" && data.extra.code) ||
    undefined;

  // Network/timeout (no response) - ONLY for axios/network errors
  if (!res) {
    const isTimeout = e?.code === "ECONNABORTED";
    return {
      status: undefined,
      code: isTimeout ? "timeout" : "network_error",
      message: isTimeout
        ? "درخواست بیش از حد طول کشید. دوباره تلاش کنید."
        : "ارتباط با سرور برقرار نشد. اینترنت یا سرور را بررسی کنید.",
      fieldErrors: undefined,
      isNetworkError: true,
      raw: e,
    };
  }

  // ✅ Validation: messages are inside extra.fields (your backend shape)
  const fields = data?.extra?.fields;

  if (fields) {
    if (Array.isArray(fields)) {
      const first = pickFirstFieldMessage(fields);
      return {
        status,
        code: code || "validation_error",
        message: first || "اطلاعات فرم معتبر نیست.",
        fieldErrors: undefined,
        isNetworkError: false,
        raw: data,
      };
    }

    if (typeof fields === "object") {
      const fieldErrors = {};
      const allMessages = [];

      for (const [field, value] of Object.entries(fields)) {
        let msgs = [];

        if (Array.isArray(value)) {
          msgs = value
            .map((v) => (typeof v === "string" ? v.trim() : String(v)))
            .filter((v) => typeof v === "string" && v.trim());
        } else if (typeof value === "string") {
          const s = value.trim();
          msgs = s ? [s] : [];
        } else if (value == null) {
          msgs = [];
        } else if (typeof value === "object") {
          const nestedFirst = pickFirstFieldMessage(value);
          msgs = nestedFirst ? [nestedFirst] : [JSON.stringify(value)];
        } else {
          msgs = [String(value)];
        }

        if (msgs.length) {
          fieldErrors[field] = msgs;
          allMessages.push(...msgs);
        } else {
          fieldErrors[field] = [];
        }
      }

      const first = pickFirstFieldMessage(fields) || allMessages[0];

      return {
        status,
        code: code || "validation_error",
        // ✅ FIX: اگر fields پیام واقعی نداشت، message را null بگذار تا code ترجمه شود
        message: first || null,
        fieldErrors,
        isNetworkError: false,
        raw: data,
      };
    }
  }

  // Non-validation
  const message =
    (typeof data?.message === "string" && data.message) ||
    (typeof data?.detail === "string" && data.detail) ||
    "خطایی رخ داد.";

  return {
    status,
    code,
    message,
    fieldErrors: undefined,
    isNetworkError: false,
    raw: data,
  };
}